/**
 * PCF Reconciliation Types — Conciliacion fondeo UNE-Credito -> FSA.
 *
 * Mismo matcher cross-account que FSA, direccion invertida (cargo FMX + abono FSA).
 * Ver SPEC_MATCHER_CROSS_ACCOUNT_UNE.md.
 */

import type { SummaryColumn, ReconciliationConfig } from './collector-reconciliation.types';

// Re-export shared types
export type { SummaryColumn };
export { MONTH_NAMES } from './collector-reconciliation.types';

// ─── Conteos por status ─────────────────────────────────────────────────────

export interface PCFReconciliationCounts {
  ok: number;
  salioSinEntrar: number;
  entroSinSalir: number;
  sinBanco: number;
  sinCuadrar: number;
}

/** Generic detail row */
export type PCFDetailRow = Record<string, unknown>;

export interface PCFReconciliationMonth {
  ok: number;
  salioSinEntrar: number;
  entroSinSalir: number;
  sinBanco: number;
  sinCuadrar: number;
  detalles: {
    ok: PCFDetailRow[];
    salioSinEntrar: PCFDetailRow[];
    entroSinSalir: PCFDetailRow[];
    sinBanco: PCFDetailRow[];
    sinCuadrar: PCFDetailRow[];
  };
}

// ─── Response completa ──────────────────────────────────────────────────────

export interface PCFReconciliationResumen {
  year: number;
  direction: 'FSA_A_FMX' | 'FMX_A_FSA';
  totalTransferencias: number;
  totalMovimientosBanco: number;
  timings?: Record<string, number>;
  totals: PCFReconciliationCounts;
}

export interface PCFReconciliationData {
  resumen: PCFReconciliationResumen;
  meses: Record<string, PCFReconciliationMonth>;
}

// ─── Registro de conciliacion ───────────────────────────────────────────────

export interface PCFRegisterRequest {
  year: number;
  month: number | null;
  totals: PCFReconciliationCounts;
  status: 'LIMPIO' | 'CON_DISCREPANCIAS';
  notes?: string;
}

// ─── UI state ───────────────────────────────────────────────────────────────

export interface PCFDrillDownSelection {
  month: number;
  statusKey: string;
}

// ─── Column config ──────────────────────────────────────────────────────────

export const PCF_SUMMARY_COLUMNS: SummaryColumn[] = [
  {
    key: 'ok', label: 'OK', color: 'green',
    tooltip: {
      title: 'Fondeo verificado en ambas cuentas',
      description: 'El fondeo declarado tiene par completo en el banco: cargo en Fiado MX y abono en Fiado SA, misma fecha, mismo monto, misma referencia bancaria.',
    },
  },
  {
    key: 'salioSinEntrar', label: 'Solo UNE', color: 'red',
    tooltip: {
      title: 'Dinero salio de UNE (FMX) pero no llego a FSA',
      description: 'Existe cargo en Fiado MX (UNE) con el monto y fecha del fondeo declarado, pero no aparece el abono correspondiente en Fiado SA.',
      conditions: [
        { label: 'Posibles causas', items: ['El fondeo salio a un destinatario distinto de FSA', 'Retraso en la contrapartida', 'Error operativo'], severity: 'error' },
      ],
    },
  },
  {
    key: 'entroSinSalir', label: 'Solo FSA', color: 'rose',
    tooltip: {
      title: 'Dinero aparecio en FSA sin origen conocido',
      description: 'Existe abono en Fiado SA con el monto y fecha del fondeo declarado, pero no aparece el cargo correspondiente en Fiado MX (UNE).',
      conditions: [
        { label: 'Posibles causas', items: ['El fondeo vino de una cuenta distinta a FMX', 'Imputacion bancaria erronea', 'Error operativo'], severity: 'error' },
      ],
    },
  },
  {
    key: 'sinBanco', label: 'Sin banco', color: 'amber',
    tooltip: {
      title: 'El fondeo declarado no aparece en el banco',
      description: 'No existe ni cargo en Fiado MX ni abono en Fiado SA con el monto y fecha declarados.',
      conditions: [
        { label: 'Posibles causas', items: ['El fondeo no se ejecuto', 'Fecha o monto capturados con error', 'Traspaso pendiente de reflejar en el edo cta'], severity: 'error' },
      ],
    },
  },
  {
    key: 'sinCuadrar', label: 'Sin cuadrar', color: 'slate',
    tooltip: {
      title: 'Falta informacion bancaria del mes',
      description: 'El estado de cuenta de Fiado SA o Fiado MX del mes no esta cargado o clasificado todavia.',
    },
  },
];

// ─── Entity config (for comparison modal) ───────────────────────────────────

export const PCF_RECONCILIATION_CONFIG: ReconciliationConfig = {
  label: 'Fondeo FMX -> FSA',
  description: 'Cruza fondeos declarados de UNE-Credito con el par bancario cargo FMX + abono FSA',
  color: 'violet',
  entityA: {
    name: 'Fondeo declarado',
    color: 'cyan',
    fields: [
      { key: 'transferId', label: 'Funding ID', format: 'mono' },
      { key: 'transferDate', label: 'Fecha', format: 'date' },
      { key: 'totalAmountMxn', label: 'Monto MXN', format: 'currency' },
      { key: 'bankReference', label: 'Clave Rastreo', format: 'mono' },
      { key: 'transferStatus', label: 'Status' },
      { key: 'creditsCount', label: '# Creditos' },
    ],
  },
  entityB: {
    name: 'Par bancario FMX -> FSA',
    color: 'violet',
    fields: [
      { key: 'salidaCuenta', label: 'Cuenta salida' },
      { key: 'salidaFecha', label: 'Fecha salida', format: 'date' },
      { key: 'salidaMonto', label: 'Monto salida', format: 'currency' },
      { key: 'salidaReferencia', label: 'Ref. salida' },
      { key: 'salidaCodigo', label: 'Codigo salida' },
      { key: 'entradaCuenta', label: 'Cuenta entrada' },
      { key: 'entradaFecha', label: 'Fecha entrada', format: 'date' },
      { key: 'entradaMonto', label: 'Monto entrada', format: 'currency' },
      { key: 'entradaReferencia', label: 'Ref. entrada' },
    ],
  },
  matchStatuses: ['ok'],
};
